import { useEffect, useState } from 'react'
import { lumi } from '@/lib/lumi'

const MONICA_FALLBACK = {
  nome: 'Mônica Maria',
  titulo: 'Aromaterapeuta e Terapeuta Integrativa',
  foto: '',
}

interface DadosConsultora {
  nome: string
  titulo: string
  foto: string
}

interface Props {
  consultora: string
  modo?: 'intro' | 'resultado' | 'identidade'
  mensagemFixa?: string
  textoEncerramento?: string
}

// Timestamp fixo por sessão para cache-busting sem re-render infinito
const SESSION_TS = Date.now()

function AvatarConsultora({ foto, nome, size = 80 }: { foto: string; nome: string; size?: number }) {
  const inicial = (nome || '?').charAt(0).toUpperCase()
  const [erroImg, setErroImg] = useState(false)

  const temFoto = !!(foto && foto.trim()) && !erroImg

  if (temFoto) {
    // Cache-busting estável: usa timestamp calculado uma única vez na sessão
    const fotoLimpa = foto.trim()
    const src = fotoLimpa.includes('?')
      ? `${fotoLimpa}&v=${SESSION_TS}`
      : `${fotoLimpa}?v=${SESSION_TS}`
    return (
      <img
        src={src}
        alt={nome}
        onError={() => setErroImg(true)}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          border: '2px solid #A8DFC4',
          flexShrink: 0,
        }}
      />
    )
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: '#2D6A4F',
        border: '2px solid #A8DFC4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 700,
        fontSize: size * 0.42,
        flexShrink: 0,
      }}
    >
      {inicial}
    </div>
  )
}

const MENSAGEM_FIXA =
  'Que incrível que você chegou aqui! Após preencher o diagnóstico entrarei em contato pelo WhatsApp para te orientar, esclarecer suas dúvidas e te ajudar a cuidar de você.'

export default function BlocoApresentacaoConsultora({ consultora, modo = 'intro', mensagemFixa, textoEncerramento }: Props) {
  const [dados, setDados] = useState<DadosConsultora | null>(null)
  const [carregado, setCarregado] = useState(false)

  useEffect(() => {
    void carregar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultora])

  async function carregar() {
    const slug = (consultora || '').toLowerCase().trim()

    if (!slug || slug === 'geral') {
      setDados(MONICA_FALLBACK)
      setCarregado(true)
      return
    }

    try {
      const resp = await lumi.entities.consultoras.list({
        filter: { usuario: slug },
        limit: 1,
      })
      const c = resp?.list?.[0]
      if (c) {
        setDados({
          nome: c.nome || MONICA_FALLBACK.nome,
          titulo: c.tituloProfissional || '',
          foto: c.fotoPerfil || c.foto || '',
        })
      } else {
        setDados(MONICA_FALLBACK)
      }
    } catch {
      setDados(MONICA_FALLBACK)
    } finally {
      setCarregado(true)
    }
  }

  if (!carregado || !dados || !dados.nome) return null

  // Modo "identidade" — foto, nome e título no resultado sem óleos
  if (modo === 'identidade') {
    const temFoto = !!(dados.foto && dados.foto.trim())
    return (
      <div style={{ margin: '24px 0' }}>
        <div
          style={{
            backgroundColor: '#F8F4EF',
            borderRadius: 8,
            padding: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            maxWidth: 600,
            margin: '0 auto',
          }}
        >
          {temFoto ? <AvatarConsultora foto={dados.foto} nome={dados.nome} size={80} /> : null}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 20,
                fontWeight: 600,
                color: '#2D6A4F',
                lineHeight: 1.3,
              }}
            >
              {dados.nome}
            </p>
            {dados.titulo ? (
              <p
                style={{
                  margin: '4px 0 0',
                  fontSize: 15,
                  fontWeight: 300,
                  color: '#666666',
                  lineHeight: 1.3,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {dados.titulo}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    )
  }

  // Modo "resultado" — bloco de encerramento
  if (modo === 'resultado') {
    return (
      <div style={{ margin: '24px 0' }}>
        <div style={{ height: 1, backgroundColor: '#A8DFC4', marginBottom: 20 }} />
        <div
          style={{
            backgroundColor: '#F8F4EF',
            borderRadius: 8,
            padding: 16,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 16,
            maxWidth: 600,
            margin: '0 auto',
          }}
        >
          <AvatarConsultora foto={dados.foto} nome={dados.nome} size={80} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                fontSize: 16,
                fontStyle: 'italic',
                color: '#444444',
                fontWeight: 300,
                lineHeight: 1.6,
                margin: 0,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              {textoEncerramento ?? (
                <>
                  🌿 Você já descobriu quais óleos essenciais o seu corpo e mente precisam. Agora é só aguardar que em breve você receberá uma mensagem minha no WhatsApp com orientações mais detalhadas de como usar cada um, qual protocolo seguir e como adquirir.
                  <br /><br />
                  Cuide-se com carinho.<br />
                  Uma gota muda tudo. ✨
                </>
              )}
            </p>
            <p
              style={{
                marginTop: 8,
                marginBottom: 0,
                fontSize: 14,
                fontWeight: 300,
                fontStyle: 'normal',
                color: '#666666',
                lineHeight: 1.4,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              {dados.nome}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Modo "intro" — bloco simplificado
  return (
    <div style={{ marginBottom: 24, maxWidth: 600, marginLeft: 'auto', marginRight: 'auto' }}>
      <div
        style={{
          backgroundColor: '#F8F4EF',
          borderRadius: 8,
          padding: 16,
        }}
      >
        {/* Foto + Nome + Título */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <AvatarConsultora foto={dados.foto} nome={dados.nome} size={80} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 20,
                fontWeight: 600,
                color: '#2D6A4F',
                lineHeight: 1.3,
              }}
            >
              {dados.nome}
            </p>
            {dados.titulo && (
              <p
                style={{
                  margin: '4px 0 0',
                  fontSize: 15,
                  fontWeight: 300,
                  color: '#666666',
                  lineHeight: 1.3,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {dados.titulo}
              </p>
            )}
          </div>
        </div>

        {/* Mensagem fixa */}
        <p
          style={{
            marginTop: 14,
            marginBottom: 0,
            fontSize: 16,
            fontStyle: 'italic',
            fontWeight: 300,
            color: '#444444',
            lineHeight: 1.7,
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          {mensagemFixa ?? MENSAGEM_FIXA}
        </p>
      </div>

      {/* Separador */}
      <div style={{ height: 1, backgroundColor: '#A8DFC4', marginTop: 16 }} />
    </div>
  )
}
